import React, {
  DetailedHTMLProps,
  AnchorHTMLAttributes,
  forwardRef,
  useMemo,
  ReactNode,
} from 'react';
import { useFocusRing } from '@react-aria/focus';
import { mergeProps } from '@react-aria/utils';
import classnames from 'classnames';

import styles from './index.module.css';

export type RSKantanIconButtonAnchorProps = DetailedHTMLProps<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  HTMLAnchorElement
> & {
  variant?: 'black';
  isBlock?: boolean;
  isDisabled?: boolean;
  icon?: ReactNode;
};

export const RSKantanIconButtonAnchor = forwardRef<
  HTMLAnchorElement,
  RSKantanIconButtonAnchorProps
>(
  (
    {
      variant = 'black',
      isBlock = false,
      isDisabled = false,
      icon,
      children,
      ...props
    },
    ref,
  ) => {
    const { isFocusVisible, focusProps } = useFocusRing();

    const color = '#0066FF';

    const wrapperHeightClasses = useMemo(() => {
      switch (variant) {
        case 'black':
          return ['h-48', 'pc:h-44'];
      }
    }, [variant]);

    const buttonSizeClasses = useMemo(() => {
      switch (variant) {
        case 'black':
          return ['px-2'];
      }
    }, [variant]);

    const buttonColorClasses = useMemo(() => {
      switch (variant) {
        case 'black':
          return [
            !isDisabled ? 'bg-black' : 'bg-gray-10',
            !isDisabled ? 'text-white' : 'text-gray-200',
            !isDisabled ? 'border-none' : 'border-gray-10',
          ];
      }
    }, [variant, isDisabled]);

    return (
      <>
        <div
          className={classnames(
            'wrapper',
            isFocusVisible && 'focus-visible',
            !isBlock ? 'inline-block' : ['block', 'w-full'],
            'relative',
            wrapperHeightClasses,
            'rounded-15',
            'after:transition',
            // cursor-event-noneとcursor-not-allowedを同じ要素で利用できないため
            !isDisabled ? 'cursor-pointer' : 'cursor-not-allowed',
          )}
        >
          <a
            {...mergeProps(props, focusProps)}
            ref={ref}
            className={classnames(
              'button',
              'relative',
              buttonSizeClasses,
              'box-border',
              'w-full',
              'h-full',
              'flex',
              'justify-center',
              'items-center',
              'border',
              'border-solid',
              'rounded-15',
              'outline-none',
              'focus:outline-none',
              buttonColorClasses,
              'no-underline',
              'z-1',
              'transition',
              icon != null && 'gap-0.5',
              !isDisabled && styles['button--default'],
              isDisabled && 'pointer-events-none',
            )}
            tabIndex={!isDisabled ? undefined : -1}
          >
            {icon && <div className="flex-none">{icon}</div>}
            <div className="rs-font text-16 pc:text-15 whitespace-nowrap font-bold">
              {children}
            </div>
          </a>
        </div>
        <style jsx>{`
          .wrapper::after {
            content: '';
            box-sizing: border-box;
            position: absolute;
            width: calc(100% + 8px);
            height: calc(100% + 8px);
            top: -4px;
            left: -4px;
            border-style: solid;
            border-width: 2px;
            border-color: transparent;
            border-radius: 18px;
          }

          .wrapper.focus-visible::after {
            border-color: ${color};
          }

          .button.bg-primary {
            background-color: ${color};
          }

          .button.text-primary {
            color: ${color};
          }

          .button.border-primary {
            border-color: ${color};
          }
        `}</style>
      </>
    );
  },
);
